import React from "react";
import PowerUpToken from "../PowerUpToken";

// The "how to play" body, shared by the in-game HelpModal (a dimmed modal over
// the match) and the shell /help section (a floating section modal).

// One power-up row: the token exactly as the arena draws it (same design
// variant, same animation), then a short description. `icon` is the svg
// basename under /svg/powerup/; `color` is its affect group.
function Pu({ icon, color, name, children }: { icon: string; color: "green" | "red" | "blue"; name: string; children: React.ReactNode }) {
  return (
    // Fixed row height (taller than a 2-line description) with the token centered,
    // so every token is evenly spaced no matter how many lines the text wraps to.
    <li style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 48 }}>
      <PowerUpToken icon={icon} color={color} size={42} />
      <span>
        <b>{name}</b>: {children}
      </span>
    </li>
  );
}

// Flex column with a fixed gap so the vertical distance between rows is identical
// even when a description wraps to two lines.
const listStyle: React.CSSProperties = { listStyle: "none", paddingLeft: 0, margin: "6px 0 0", display: "flex", flexDirection: "column", gap: 2 };
// Three color groups as flex columns: side-by-side on wide screens, wrapping to
// fewer columns (down to one) on narrow/mobile widths.
const columnsStyle: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: 24, margin: "6px 0 4px" };
const groupStyle: React.CSSProperties = { flex: "1 1 240px", minWidth: 0 };
const groupHeadStyle: React.CSSProperties = { margin: "0 0 2px", fontWeight: 700 };
// Separates the three sections (rules / power-ups / controls) with a line + space.
const dividerStyle: React.CSSProperties = { border: "none", borderTop: "1px solid rgba(255,255,255,0.1)", margin: "24px 0 20px" };

export default function HelpContent() {
  return (
    <>
      <p>You can't stop or slow down. Turn <b>left</b> or <b>right</b> and don't crash into walls, other worms, or your own trail.</p>
      <ul>
        <li>Worms leave little <b>gaps</b>. Slip through them.</li>
        <li><b>Respawn</b> (Play Now, game rooms): crash and you are back in 2 seconds. A round lasts 2 minutes. +1 for every worm that crashes into your trail, +1 for every 10 seconds alive, -1 for crashing on your own.</li>
        <li>Ranked and the Royale: last worm alive wins the round. First to the target score wins the match.</li>
      </ul>

      <hr style={dividerStyle} />
      <h3>Power-ups</h3>
      <p>Drive over them to grab them. <b style={{ color: "#2fbf57" }}>Green</b> helps whoever grabs it, <b style={{ color: "#ff3b30" }}>red</b> hurts everyone else, <b style={{ color: "#4f86ff" }}>blue</b> affects everyone. Your active ones show on the left in the same colors.</p>

      <div style={columnsStyle}>
        <div style={groupStyle}>
          <p style={{ ...groupHeadStyle, color: "#2fbf57" }}>Green (helps you)</p>
          <ul style={listStyle}>
            <Pu icon="slow" color="green" name="Slow">you slow down and get more control.</Pu>
            <Pu icon="slim" color="green" name="Slim">thinner worm, easier to slip through gaps.</Pu>
            <Pu icon="quickturn" color="green" name="Quick turn">you turn sharper.</Pu>
            <Pu icon="superjump" color="green" name="Super jump">fly over the lines for a moment.</Pu>
            <Pu icon="twin" color="green" name="Twin">spawns a second worm you also control.</Pu>
          </ul>
        </div>

        <div style={groupStyle}>
          <p style={{ ...groupHeadStyle, color: "#ff3b30" }}>Red (hurts everyone else)</p>
          <ul style={listStyle}>
            <Pu icon="speed" color="red" name="Speed">enemies speed up.</Pu>
            <Pu icon="fat" color="red" name="Fat">enemies get thick and clumsy.</Pu>
            <Pu icon="slowturn" color="red" name="Slow turn">enemies steer sluggishly.</Pu>
            <Pu icon="switchkeys" color="red" name="Reversed keys">enemies' left and right controls flip.</Pu>
            <Pu icon="drunk" color="red" name="Drunk">don't drink and drive.</Pu>
            <Pu icon="tronturn" color="red" name="Tron turn">enemies can only make sharp 90° turns.</Pu>
          </ul>
        </div>

        <div style={groupStyle}>
          <p style={{ ...groupHeadStyle, color: "#4f86ff" }}>Blue (affects everyone)</p>
          <ul style={listStyle}>
            <Pu icon="switcharoonie" color="blue" name="Switcharoonie">everyone randomly swaps worms.</Pu>
            <Pu icon="wallhack" color="blue" name="Wall hack">everyone can pass through walls.</Pu>
          </ul>
        </div>
      </div>

      <hr style={dividerStyle} />
      <h3>Controls</h3>
      <p className="app-muted">Each player picks two keys, one to turn each way. On a phone, tap your half of the screen.</p>
    </>
  );
}
